import React, { useEffect } from 'react';
import { Box, Typography, InputBase } from '@mui/material';
import { useField } from 'formik';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import SpaIcon from '@mui/icons-material/Spa';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import WavesIcon from '@mui/icons-material/Waves';

const HERO_IMAGE = '/tiki-hero.webp';

const FONTS_URL =
    'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&family=Manrope:wght@400;500;600&display=swap';

export const tiki = {
    headline: '"Plus Jakarta Sans", sans-serif',
    body: '"Manrope", sans-serif',
    colors: {
        surface: '#011202',
        onSurface: '#d5f0cd',
        primary: '#ff906d',
        primaryContainer: '#ff784e',
        onPrimary: '#5b1500',
        secondary: '#ff6d8d',
        error: '#ff716c',
        text: 'rgba(1,18,2,0.8)',
        textMuted: 'rgba(1,18,2,0.6)',
        textFaint: 'rgba(1,18,2,0.4)',
        textGhost: 'rgba(1,18,2,0.3)',
        inputBg: 'rgba(1,18,2,0.05)',
        inputBorder: 'rgba(1,18,2,0.1)',
    },
};

const inputIconSx = {
    position: 'absolute',
    left: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    color: tiki.colors.textFaint,
    fontSize: 22,
};

export function TikiInput({ name, label, type = 'text', placeholder, icon, right }) {
    const [field, meta] = useField(name);
    return (
        <Box sx={{ mb: 3 }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 0.5,
                    mb: 1,
                }}
            >
                <Typography
                    component="label"
                    htmlFor={name}
                    sx={{
                        fontFamily: tiki.headline,
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        color: tiki.colors.text,
                    }}
                >
                    {label}
                </Typography>
                {right}
            </Box>
            <Box sx={{ position: 'relative' }}>
                {React.cloneElement(icon, { sx: { ...inputIconSx, ...icon.props.sx } })}
                <InputBase
                    id={name}
                    type={type}
                    placeholder={placeholder}
                    {...field}
                    sx={{
                        width: '100%',
                        pl: 6,
                        pr: 2,
                        py: 1.75,
                        fontFamily: tiki.body,
                        fontSize: '1rem',
                        color: tiki.colors.surface,
                        bgcolor: tiki.colors.inputBg,
                        border: `1px solid ${tiki.colors.inputBorder}`,
                        borderRadius: '0.75rem',
                        transition: 'all 0.3s',
                        '&.Mui-focused': {
                            outline: `2px solid ${tiki.colors.primary}`,
                            borderColor: 'transparent',
                        },
                        '& input::placeholder': {
                            color: tiki.colors.textGhost,
                            opacity: 1,
                        },
                    }}
                />
            </Box>
            {meta.touched && meta.error && (
                <Typography
                    sx={{
                        fontFamily: tiki.body,
                        fontSize: '0.75rem',
                        color: tiki.colors.error,
                        mt: 0.5,
                        px: 0.5,
                    }}
                >
                    {meta.error}
                </Typography>
            )}
        </Box>
    );
}

export function TikiButton({ disabled, type = 'submit', onClick, children }) {
    return (
        <Box
            component="button"
            type={type}
            disabled={disabled}
            onClick={onClick}
            sx={{
                width: '100%',
                py: 2,
                px: 3,
                mt: 4,
                background: `linear-gradient(135deg, ${tiki.colors.primary}, ${tiki.colors.primaryContainer})`,
                color: tiki.colors.onPrimary,
                fontFamily: tiki.headline,
                fontWeight: 800,
                fontSize: '1.125rem',
                border: 'none',
                borderRadius: '0.75rem',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                boxShadow: '0 10px 30px rgba(255,144,109,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                transition: 'all 0.2s',
                '&:hover:not(:disabled)': {
                    boxShadow: '0 15px 40px rgba(255,144,109,0.4)',
                    transform: 'scale(1.02)',
                },
                '&:active:not(:disabled)': {
                    transform: 'scale(0.98)',
                },
            }}
        >
            {children}
        </Box>
    );
}

function useFonts() {
    useEffect(() => {
        if (document.querySelector(`link[href="${FONTS_URL}"]`)) return;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = FONTS_URL;
        document.head.appendChild(link);
    }, []);
}

export default function TikiLayout({ children, showFooterIcons }) {
    useFonts();

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                display: 'flex',
                minHeight: '100vh',
                bgcolor: tiki.colors.surface,
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    display: { xs: 'none', md: 'block' },
                    width: '60%',
                    position: 'relative',
                    backgroundImage: `url('${HERO_IMAGE}')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: `linear-gradient(to top right, ${tiki.colors.surface}, transparent, transparent)`,
                        opacity: 0.8,
                    }}
                />

                <Box
                    sx={{
                        position: 'absolute',
                        top: 48,
                        left: 48,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    <LocalFireDepartmentIcon sx={{ color: tiki.colors.primary, fontSize: 28 }} />
                    <Typography
                        sx={{
                            fontFamily: tiki.headline,
                            fontWeight: 700,
                            fontSize: '0.875rem',
                            letterSpacing: '0.15em',
                            color: tiki.colors.onSurface,
                            textTransform: 'uppercase',
                        }}
                    >
                        Aloha Experience
                    </Typography>
                </Box>

                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 48,
                        left: 0,
                        right: 0,
                        px: 6,
                        textAlign: 'left',
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: tiki.headline,
                            fontWeight: 900,
                            fontSize: '3.75rem',
                            color: tiki.colors.primary,
                            letterSpacing: '-0.03em',
                            lineHeight: 1,
                            textShadow: '0 4px 20px rgba(0,0,0,0.5)',
                        }}
                    >
                        Fireknife
                    </Typography>
                    <Typography
                        sx={{
                            mt: 2,
                            fontFamily: tiki.body,
                            fontSize: '1.25rem',
                            color: tiki.colors.onSurface,
                            opacity: 0.8,
                            maxWidth: 400,
                            lineHeight: 1.6,
                        }}
                    >
                        Where the heat of the hearth meets the soul of the island.
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    width: { xs: '100%', md: '40%' },
                    bgcolor: tiki.colors.onSurface,
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: { xs: 4, md: 8 },
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: -96,
                        right: -96,
                        width: 256,
                        height: 256,
                        bgcolor: tiki.colors.primary,
                        opacity: 0.1,
                        borderRadius: '50%',
                        filter: 'blur(48px)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -96,
                        left: -96,
                        width: 256,
                        height: 256,
                        bgcolor: tiki.colors.secondary,
                        opacity: 0.1,
                        borderRadius: '50%',
                        filter: 'blur(48px)',
                    }}
                />

                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 420,
                        position: 'relative',
                        zIndex: 1,
                        textAlign: 'left',
                    }}
                >
                    <Box
                        sx={{
                            display: { xs: 'flex', md: 'none' },
                            flexDirection: 'column',
                            alignItems: 'center',
                            mb: 6,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: tiki.headline,
                                fontWeight: 900,
                                fontSize: '2.25rem',
                                color: tiki.colors.primary,
                                letterSpacing: '-0.03em',
                            }}
                        >
                            Fireknife
                        </Typography>
                    </Box>

                    {children}
                </Box>

                {showFooterIcons && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 32,
                            right: 32,
                            display: 'flex',
                            gap: 2,
                            opacity: 0.1,
                        }}
                    >
                        <SpaIcon sx={{ color: tiki.colors.surface, fontSize: 36 }} />
                        <WbSunnyIcon sx={{ color: tiki.colors.surface, fontSize: 36 }} />
                        <WavesIcon sx={{ color: tiki.colors.surface, fontSize: 36 }} />
                    </Box>
                )}
            </Box>
        </Box>
    );
}
